import React, { useEffect, useState } from "react";
import { Box, Menu, MenuItem, Popover, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AppTable from "../../common/app-table";
import AppDatePicker from "../../common/app-date-picker";
import TableSearchInput from "../TableSearchInput";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import { USERS_ENDPOINT } from "../../config/urls";

const CHANNEL_OPTIONS = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "rcs", label: "RCS" },
];
const statusLabel = (value) => value.charAt(0).toUpperCase() + value.slice(1).replaceAll("_", " ");

// "Label ▾" chip; shows the picked value and a clear (×) once set
const FilterChip = ({ label, valueLabel, onOpen, onClear }) => (
  <Box
    component="button"
    type="button"
    onClick={onOpen}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1.75,
      py: 1.1,
      borderRadius: "10px",
      border: "1px solid",
      borderColor: valueLabel ? "#14204D" : "#D8DAE1",
      bgcolor: valueLabel ? "#EEF1FA" : "#fff",
      color: valueLabel ? "#14204D" : "#3B4250",
      fontFamily: "inherit",
      ...typography.bodyLarge,
      fontWeight: 500,
      cursor: "pointer",
      whiteSpace: "nowrap",
    }}
  >
    {valueLabel ? `${label}: ${valueLabel}` : label}
    {valueLabel ? (
      <CloseIcon
        sx={{ fontSize: 16 }}
        onClick={(e) => {
          e.stopPropagation();
          onClear();
        }}
      />
    ) : (
      <span style={{ fontSize: 11 }}>&#9662;</span>
    )}
  </Box>
);

// Dropdown chip over a fixed list of { value, label } options
const SelectFilter = ({ label, options, value, onChange }) => {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <FilterChip
        label={label}
        valueLabel={options.find((option) => option.value === value)?.label}
        onOpen={(e) => setAnchor(e.currentTarget)}
        onClear={() => onChange("")}
      />
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} PaperProps={{ sx: { maxHeight: 320 } }}>
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => {
              onChange(option.value);
              setAnchor(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const SEARCH_MAX_LENGTH = 50; // matches TableSearchInput's counter (N/50)

// Searchable, filterable, paginated list used by both Campaigns (Beta) tabs.
// The endpoint takes limit/offset/search/channel/status/created_by/from_date/to_date and returns { count, results }.
const PagedListTab = ({ endpoint, columns, statusOptions, searchPlaceholder, searchTooltip, emptyMessage, rowKey, renderExpanded, isRowExpandedByDefault }) => {
  const { getValidToken } = useAuth();

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [controller, setController] = useState({ page: 0, rowsPerPage: 20 });

  const [searchQuery, setSearchQuery] = useState("");
  const [dateAnchor, setDateAnchor] = useState(null);
  const [channelFilter, setChannelFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [createdByFilter, setCreatedByFilter] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [createdByList, setCreatedByList] = useState([]);

  const authHeaders = async () => ({
    Authorization: `Bearer ${await getValidToken()}`,
    "Content-Type": "application/json",
    "X-Tenant-Id": localStorage.getItem("tenant"),
  });

  useEffect(() => {
    const fetchCreatedByList = async () => {
      try {
        const response = await fetch(USERS_ENDPOINT, { headers: await authHeaders() });
        const data = await response.json();
        setCreatedByList(data.results || []);
      } catch (error) {
        console.error(error);
        setCreatedByList([]);
      }
    };
    fetchCreatedByList();
  }, []);

  useEffect(() => {
    const fetchRows = async () => {
      setLoading(true);
      const params = new URLSearchParams({
        limit: controller.rowsPerPage.toString(),
        offset: (controller.page * controller.rowsPerPage).toString(),
        search: searchQuery,
        channel: channelFilter,
        status: statusFilter,
        created_by: createdByFilter,
        from_date: fromDate ? fromDate.format("YYYY-MM-DD") : "",
        to_date: toDate ? toDate.format("YYYY-MM-DD") : "",
      });
      try {
        const response = await fetch(`${endpoint}?${params}`, { headers: await authHeaders() });
        if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
        const data = await response.json();
        setRows(data.results || []);
        setRowCount(data.count || 0);
      } catch (error) {
        console.error(error);
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    };
    // Small delay so typing in the search box doesn't fire a request per keystroke
    const timer = setTimeout(fetchRows, 300);
    return () => clearTimeout(timer);
  }, [endpoint, controller, searchQuery, channelFilter, statusFilter, createdByFilter, fromDate, toDate]);

  const resetPage = () => setController((prev) => ({ ...prev, page: 0 }));
  const withPageReset = (setter) => (value) => {
    setter(value);
    resetPage();
  };

  const dateLabel =
    fromDate || toDate
      ? `${fromDate ? fromDate.format("D MMM") : "…"} – ${toDate ? toDate.format("D MMM") : "…"}`
      : null;

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2, flexWrap: "wrap" }}>
        <TableSearchInput
          searchQuery={searchQuery}
          onSearchChange={(e) => withPageReset(setSearchQuery)(e.target.value.slice(0, SEARCH_MAX_LENGTH))}
          charCount={searchQuery.length}
          tooltip={searchTooltip}
          placeholder={searchPlaceholder}
          sx={{ width: { xs: "100%", md: 300 }, flexShrink: 0 }}
        />
        <SelectFilter label="Channel" options={CHANNEL_OPTIONS} value={channelFilter} onChange={withPageReset(setChannelFilter)} />
        <SelectFilter
          label="Progress"
          options={statusOptions.map((value) => ({ value, label: statusLabel(value) }))}
          value={statusFilter}
          onChange={withPageReset(setStatusFilter)}
        />
        <SelectFilter
          label="Created by"
          options={createdByList.map((creator) => ({ value: creator.id, label: creator.name || creator.email }))}
          value={createdByFilter}
          onChange={withPageReset(setCreatedByFilter)}
        />
        <FilterChip
          label="Date range"
          valueLabel={dateLabel}
          onOpen={(e) => setDateAnchor(e.currentTarget)}
          onClear={() => {
            setFromDate(null);
            setToDate(null);
            resetPage();
          }}
        />
        <Popover
          open={!!dateAnchor}
          anchorEl={dateAnchor}
          onClose={() => setDateAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          PaperProps={{ sx: { p: 2, mt: 0.5, borderRadius: "12px" } }}
        >
          <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: "#3B4250", mb: 1 }}>Date range</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", "& > *": { width: 160 } }}>
              <Box><AppDatePicker value={fromDate} onChange={withPageReset(setFromDate)} placeholder="From" /></Box>
              <Box><AppDatePicker value={toDate} onChange={withPageReset(setToDate)} minDate={fromDate || undefined} placeholder="To" /></Box>
            </Box>
          </LocalizationProvider>
        </Popover>
      </Box>

      <AppTable
        columns={columns}
        rows={rows}
        rowKey={rowKey}
        loading={loading}
        emptyMessage={emptyMessage}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={(event, newPage) => setController((prev) => ({ ...prev, page: newPage }))}
        onRowsPerPageChange={(event) => setController({ page: 0, rowsPerPage: parseInt(event.target.value, 10) })}
        renderExpanded={renderExpanded}
        isRowExpandedByDefault={isRowExpandedByDefault}
      />

    </>
  );
};

export default PagedListTab;
